import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { ArrowLeft, ChevronDown, ChevronUp, Star, Users, Briefcase, Clock, CreditCard, Globe, FileCheck, RefreshCw, Calendar, MessageCircle, Ban, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import MobileBottomNav from '../components/MobileBottomNav';
import '../styles/FAQPage.css';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';

const FAQPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const seo = useSEO('faq', {
    title: 'Dubai Visa FAQ | Visa Types, Prices, Processing Time & Requirements — Registan Tourism',
    description: 'Get answers to common questions about UAE visas: family visa cost, tourist visa requirements, freelance visa, golden visa eligibility, visa change process.',
    keywords: 'dubai visa faq, UAE visa questions, how much dubai visa cost, tourist visa dubai requirements, family visa UAE'
  });

  const content = {
    en: {
      heroTitle: "Dubai & UAE Visa Services – FAQ and Types",
      mainTitle: "UAE Visa Services in Dubai",
      mainIntro: "Welcome to Registan Tours, your trusted partner for all UAE visa services. We offer fast and reliable visa processing for families, residents, professionals, and investors.",
      faqTitle: "Frequently Asked Questions (FAQ)",
      faqs: [
        {
          icon: RefreshCw,
          question: "1. Can I change my visa type in Dubai?",
          answer: "Yes, we can help you change your visa in Dubai. The visa change can be done without leaving the UAE, either by car, airline, or bus, depending on your situation and visa requirements. Our team ensures the process is smooth and fast."
        },
        {
          icon: Clock,
          question: "2. How long does it take to process a UAE visa?",
          answer: "Visa processing usually takes 24–72 hours. Urgent processing options are also available."
        },
        {
          icon: FileCheck,
          question: "3. What documents are required to apply?",
          answer: "To apply for a UAE visa, you need:\n\n• Passport copy (valid for at least 6 months)\n• Passport-size photo\n• Contact number"
        },
        {
          icon: Globe,
          question: "4. What types of visas do you offer?",
          answer: "We provide a wide range of UAE visa services, including:\n\n• UAE Family Visa – sponsor your spouse, children, or parents\n• 2-Year UAE Residence Visa – long-term residence visa for employment or business\n• 120-Day UAE Visa – extended visit visa for longer stays in Dubai and the UAE\n• UAE Job Seeker Visa – legal entry visa to search for employment in the UAE\n• 10-Year UAE Golden Visa – long-term residency visa for investors, professionals, and entrepreneurs"
        },
        {
          icon: Calendar,
          question: "5. Can the visa be extended?",
          answer: "Yes, the visa can be extended without leaving the UAE.\n\nIf the previous visa was issued through our company, the extension process is faster and easier, as your details are already in our system.\n\n📌 Required documents for extension:\n• Passport copy"
        },
        {
          icon: RefreshCw,
          question: "6. What is a visa change?",
          answer: "Visa change is a procedure to renew your stay legally. It can be done by exiting the country (bus or car) and re-entering with a new visa."
        },
        {
          icon: Clock,
          question: "7. Can I extend my visa more than once?",
          answer: "In most cases, yes. Extensions depend on your current visa type and immigration rules at the time of application."
        },
        {
          icon: MessageCircle,
          question: "8. How can I check my visa status?",
          answer: "You can contact our team via WhatsApp or Telegram to receive updates about your application status."
        },
        {
          icon: CreditCard,
          question: "9. Do you accept online payments?",
          answer: "Yes, we accept secure online payments via bank cards and other payment methods."
        },
        {
          icon: Ban,
          question: "10. Can a visa be rejected?",
          answer: "Rejection may happen due to:\n\n• Previous immigration violations\n• Errors in documents\n• Entry ban or blacklist status"
        }
      ],
      backToHome: "Back to Home",
      stillHaveQuestions: "Still have questions?",
      contactText: "Our team is available 24/7 to assist you with any queries."
    },
    ru: {
      heroTitle: "Визовые услуги в Дубае и ОАЭ – FAQ и типы виз",
      mainTitle: "Визовые услуги ОАЭ в Дубае",
      mainIntro: "Добро пожаловать в Registan Tours – вашего надежного партнера по всем визовым услугам ОАЭ. Мы предлагаем быструю и надежную обработку виз для семей, резидентов, профессионалов и инвесторов.",
      faqTitle: "Часто задаваемые вопросы (FAQ)",
      faqs: [
        {
          icon: RefreshCw,
          question: "1. Могу ли я изменить тип визы в Дубае?",
          answer: "Да, мы можем помочь вам изменить визу в Дубае. Смена визы может быть произведена без выезда из ОАЭ – на машине, самолете или автобусе, в зависимости от вашей ситуации и визовых требований. Наша команда обеспечивает быстрый и гладкий процесс."
        },
        {
          icon: Clock,
          question: "2. Сколько времени занимает оформление визы ОАЭ?",
          answer: "Оформление визы обычно занимает 24–72 часа. Также доступны варианты срочного оформления."
        },
        {
          icon: FileCheck,
          question: "3. Какие документы необходимы для подачи заявления?",
          answer: "Для подачи заявления на визу ОАЭ вам нужны:\n\n• Копия паспорта (действительного не менее 6 месяцев)\n• Фото паспортного размера\n• Контактный номер"
        },
        {
          icon: Globe,
          question: "4. Какие типы виз вы предлагаете?",
          answer: "Мы предоставляем широкий спектр визовых услуг ОАЭ:\n\n• Семейная виза ОАЭ – спонсирование супруга, детей или родителей\n• 2-летняя резидентская виза ОАЭ – долгосрочная виза для работы или бизнеса\n• 120-дневная виза ОАЭ – расширенная виза для длительного пребывания\n• Виза для поиска работы в ОАЭ – легальный въезд для поиска работы\n• 10-летняя Золотая виза ОАЭ – долгосрочное резидентство для инвесторов и профессионалов"
        },
        {
          icon: Calendar,
          question: "5. Можно ли продлить визу?",
          answer: "Да, визу можно продлить без выезда из ОАЭ.\n\nЕсли предыдущая виза была оформлена через нашу компанию, процесс продления будет быстрее и проще, так как ваши данные уже в нашей системе.\n\n📌 Необходимые документы для продления:\n• Копия паспорта"
        },
        {
          icon: RefreshCw,
          question: "6. Что такое смена визы?",
          answer: "Смена визы – это процедура легального продления пребывания. Она осуществляется путем выезда из страны (на автобусе или машине) и повторного въезда с новой визой."
        },
        {
          icon: Clock,
          question: "7. Можно ли продлевать визу несколько раз?",
          answer: "В большинстве случаев да. Продление зависит от текущего типа визы и иммиграционных правил на момент подачи заявления."
        },
        {
          icon: MessageCircle,
          question: "8. Как проверить статус визы?",
          answer: "Вы можете связаться с нашей командой через WhatsApp или Telegram для получения обновлений о статусе вашего заявления."
        },
        {
          icon: CreditCard,
          question: "9. Принимаете ли вы онлайн-платежи?",
          answer: "Да, мы принимаем безопасные онлайн-платежи банковскими картами и другими способами оплаты."
        },
        {
          icon: Ban,
          question: "10. Может ли виза быть отклонена?",
          answer: "Отказ может произойти по следующим причинам:\n\n• Предыдущие иммиграционные нарушения\n• Ошибки в документах\n• Запрет на въезд или черный список"
        }
      ],
      backToHome: "На главную",
      stillHaveQuestions: "Остались вопросы?",
      contactText: "Наша команда доступна 24/7 для помощи с любыми вопросами."
    },
    uz: {
      heroTitle: "Dubay va BAA viza xizmatlari – FAQ va viza turlari",
      mainTitle: "Dubayda BAA viza xizmatlari",
      mainIntro: "Registan Tours-ga xush kelibsiz – barcha BAA viza xizmatlari bo'yicha ishonchli hamkoringiz. Biz oilalar, rezidentlar, mutaxassislar va investorlar uchun tez va ishonchli viza rasmiylashtirish xizmatlarini taklif etamiz.",
      faqTitle: "Ko'p so'raladigan savollar (FAQ)",
      faqs: [
        {
          icon: RefreshCw,
          question: "1. Dubayda viza turini o'zgartira olamanmi?",
          answer: "Ha, biz sizga Dubayda vizangizni o'zgartirishda yordam bera olamiz. Viza o'zgartirish BAAdan chiqmasdan – mashina, samolyot yoki avtobus orqali amalga oshirilishi mumkin. Jamoamiz jarayonning tez va silliq bo'lishini ta'minlaydi."
        },
        {
          icon: Clock,
          question: "2. BAA vizasini rasmiylashtirish qancha vaqt oladi?",
          answer: "Viza rasmiylashtirish odatda 24–72 soat davom etadi. Shoshilinch rasmiylashtirish imkoniyatlari ham mavjud."
        },
        {
          icon: FileCheck,
          question: "3. Ariza topshirish uchun qanday hujjatlar kerak?",
          answer: "BAA vizasi uchun ariza topshirish uchun sizga kerak:\n\n• Pasport nusxasi (kamida 6 oy amal qilish muddati)\n• Pasport o'lchamidagi rasm\n• Aloqa raqami"
        },
        {
          icon: Globe,
          question: "4. Qanday viza turlarini taklif etasiz?",
          answer: "Biz keng doiradagi BAA viza xizmatlarini taqdim etamiz:\n\n• BAA oilaviy vizasi – turmush o'rtog'ingiz, farzandlaringiz yoki ota-onangizni homiylik qilish\n• 2 yillik BAA rezidentlik vizasi – ish yoki biznes uchun uzoq muddatli viza\n• 120 kunlik BAA vizasi – uzoq muddat qolish uchun kengaytirilgan viza\n• BAA ish qidirish vizasi – BAAda ish qidirish uchun qonuniy kirish vizasi\n• 10 yillik BAA Oltin vizasi – investorlar va mutaxassislar uchun uzoq muddatli rezidentlik"
        },
        {
          icon: Calendar,
          question: "5. Vizani uzaytirish mumkinmi?",
          answer: "Ha, vizani BAAdan chiqmasdan uzaytirish mumkin.\n\nAgar oldingi viza bizning kompaniyamiz orqali rasmiylashtirilgan bo'lsa, uzaytirish jarayoni tezroq va osonroq bo'ladi, chunki ma'lumotlaringiz allaqachon tizimimizda.\n\n📌 Uzaytirish uchun kerakli hujjatlar:\n• Pasport nusxasi"
        },
        {
          icon: RefreshCw,
          question: "6. Viza o'zgartirish nima?",
          answer: "Viza o'zgartirish – bu qonuniy ravishda qolish muddatini yangilash tartibi. Bu mamlakatdan chiqish (avtobus yoki mashina) va yangi viza bilan qayta kirish orqali amalga oshiriladi."
        },
        {
          icon: Clock,
          question: "7. Vizani bir necha marta uzaytirish mumkinmi?",
          answer: "Ko'p hollarda ha. Uzaytirish joriy viza turi va ariza topshirish vaqtidagi immigratsiya qoidalariga bog'liq."
        },
        {
          icon: MessageCircle,
          question: "8. Viza holatini qanday tekshirish mumkin?",
          answer: "Arizangiz holati haqida yangilanishlar olish uchun WhatsApp yoki Telegram orqali jamoamiz bilan bog'lanishingiz mumkin."
        },
        {
          icon: CreditCard,
          question: "9. Onlayn to'lovlarni qabul qilasizmi?",
          answer: "Ha, biz bank kartalari va boshqa to'lov usullari orqali xavfsiz onlayn to'lovlarni qabul qilamiz."
        },
        {
          icon: Ban,
          question: "10. Viza rad etilishi mumkinmi?",
          answer: "Rad etish quyidagi sabablarga ko'ra sodir bo'lishi mumkin:\n\n• Oldingi immigratsiya qoidabuzarliklari\n• Hujjatlardagi xatolar\n• Kirish taqiqi yoki qora ro'yxat holati"
        }
      ],
      backToHome: "Bosh sahifa",
      stillHaveQuestions: "Savollaringiz bormi?",
      contactText: "Jamoamiz har qanday savollar uchun 24/7 yordam berishga tayyor."
    }
  };

  const currentContent = content[language] || content.en;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-page" data-testid="faq-page">
      <Helmet>
        <link rel="canonical" href="https://registantours.com/faq" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/faq" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Animated Background */}
      <div className="faq-bg-animation">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
      </div>

      {/* Header */}
      <header className="faq-header">
        <button className="btn-back" onClick={() => navigate('/')} data-testid="back-btn">
          <ArrowLeft size={20} />
          {currentContent.backToHome}
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/faq" />

      {/* Hero Section */}
      <section className="faq-hero">
        <div className="faq-hero-badge">
          <Star size={20} />
          <span>{currentContent.heroTitle}</span>
        </div>
        <h1 className="faq-main-title">{currentContent.mainTitle}</h1>
        <p className="faq-intro">{currentContent.mainIntro}</p>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="faq-section-title">{currentContent.faqTitle}</h2>
        
        <div className="faq-list">
          {currentContent.faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const IconComponent = faq.icon;
            return (
              <div 
                key={index} 
                className={`faq-item ${isOpen ? 'open' : ''}`}
                data-testid={`faq-item-${index}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                >
                  <div className="faq-question-content">
                    <div className="faq-icon">
                      <IconComponent size={20} />
                    </div>
                    <span>{faq.question}</span>
                  </div>
                  {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="faq-cta">
        <div className="cta-content">
          <h3>{currentContent.stillHaveQuestions}</h3>
          <p>{currentContent.contactText}</p>
          <div className="cta-buttons">
            <a 
              href="https://wa.me/971569130707" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cta-btn whatsapp"
            >
              WhatsApp
            </a>
            <a 
              href="https://t.me/changeVISAoman" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cta-btn telegram"
            >
              Telegram
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="faq-footer">
        <Link to="/privacy" className="footer-privacy-box" data-testid="footer-privacy-link" style={{ marginBottom: '14px' }}>
          <Shield size={14} />
          <span>{t('privacyPolicy') || 'Privacy Policy'}</span>
        </Link>
        <p>&copy; {new Date().getFullYear()} Registan Tourism. All rights reserved.</p>
      </footer>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default FAQPage;
